import React, { useEffect, useMemo, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "plenum_admin_token";

const styles = {
    page: {
        minHeight: "100vh",
        backgroundColor: "#0B1B2E",
        color: "#F8F7F4",
        fontFamily: "'Inter Tight', sans-serif",
        padding: "48px 32px",
        boxSizing: "border-box",
    },
    container: {
        maxWidth: "1080px",
        margin: "0 auto",
    },
    header: {
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        marginBottom: "56px",
        paddingBottom: "20px",
        borderBottom: "1px solid rgba(248,247,244,0.14)",
    },
    title: {
        fontSize: "28px",
        fontWeight: 500,
        letterSpacing: "-0.03em",
        margin: 0,
    },
    subtitle: {
        fontSize: "12px",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: "#7AA6D8",
        fontFamily: "'IBM Plex Mono', monospace",
        margin: 0,
    },
    statsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "1px",
        backgroundColor: "rgba(248,247,244,0.14)",
        border: "1px solid rgba(248,247,244,0.14)",
        marginBottom: "56px",
    },
    statCell: {
        backgroundColor: "#0B1B2E",
        padding: "32px 28px",
    },
    statLabel: {
        fontSize: "11px",
        letterSpacing: "0.32em",
        textTransform: "uppercase",
        color: "#7AA6D8",
        fontFamily: "'IBM Plex Mono', monospace",
        marginBottom: "16px",
    },
    statValue: {
        fontSize: "64px",
        lineHeight: "0.9",
        fontWeight: 500,
        letterSpacing: "-0.04em",
        fontFamily: "'IBM Plex Mono', monospace",
        fontVariantNumeric: "tabular-nums",
    },
    toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "20px",
        flexWrap: "wrap",
        gap: "12px",
    },
    sectionTitle: {
        fontSize: "13px",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: "#7AA6D8",
        fontFamily: "'IBM Plex Mono', monospace",
        margin: 0,
    },
    btn: {
        backgroundColor: "#3A6FB0",
        color: "#F8F7F4",
        border: "none",
        padding: "12px 20px",
        fontSize: "13px",
        fontFamily: "'Inter Tight', sans-serif",
        fontWeight: 500,
        cursor: "pointer",
        borderRadius: "2px",
        letterSpacing: "-0.005em",
    },
    btnGhost: {
        backgroundColor: "transparent",
        color: "#7AA6D8",
        border: "1px solid rgba(248,247,244,0.14)",
        padding: "10px 16px",
        fontSize: "12px",
        fontFamily: "'IBM Plex Mono', monospace",
        cursor: "pointer",
        borderRadius: "2px",
        letterSpacing: "0.05em",
    },
    tableWrap: {
        border: "1px solid rgba(248,247,244,0.14)",
        overflowX: "auto",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "14px",
    },
    th: {
        textAlign: "left",
        padding: "14px 20px",
        fontSize: "11px",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: "#7AA6D8",
        fontFamily: "'IBM Plex Mono', monospace",
        fontWeight: 500,
        backgroundColor: "rgba(248,247,244,0.04)",
        borderBottom: "1px solid rgba(248,247,244,0.14)",
    },
    td: {
        padding: "14px 20px",
        borderBottom: "1px solid rgba(248,247,244,0.08)",
        fontFamily: "'Inter Tight', sans-serif",
    },
    tdMono: {
        padding: "14px 20px",
        borderBottom: "1px solid rgba(248,247,244,0.08)",
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: "13px",
        color: "#8A9BB2",
    },
    emptyRow: {
        padding: "40px 20px",
        textAlign: "center",
        color: "#6B7E96",
        fontStyle: "italic",
    },
    // Login
    loginWrap: {
        minHeight: "100vh",
        backgroundColor: "#0B1B2E",
        color: "#F8F7F4",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Inter Tight', sans-serif",
        padding: "24px",
    },
    loginCard: {
        width: "100%",
        maxWidth: "420px",
    },
    loginEyebrow: {
        fontSize: "11px",
        letterSpacing: "0.32em",
        textTransform: "uppercase",
        color: "#7AA6D8",
        fontFamily: "'IBM Plex Mono', monospace",
        marginBottom: "16px",
    },
    loginTitle: {
        fontSize: "32px",
        fontWeight: 500,
        letterSpacing: "-0.035em",
        marginBottom: "32px",
    },
    input: {
        width: "100%",
        backgroundColor: "transparent",
        border: "1px solid rgba(248,247,244,0.14)",
        color: "#F8F7F4",
        padding: "14px 16px",
        fontSize: "14px",
        fontFamily: "'IBM Plex Mono', monospace",
        outline: "none",
        borderRadius: "2px",
        boxSizing: "border-box",
        marginBottom: "16px",
    },
    error: {
        fontSize: "13px",
        color: "#B8702B",
        marginBottom: "12px",
        fontFamily: "'IBM Plex Mono', monospace",
    },
    footerLine: {
        marginTop: "48px",
        fontSize: "12px",
        color: "#6B7E96",
        fontFamily: "'IBM Plex Mono', monospace",
    },
};

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("de-CH", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const LoginScreen = ({ onAuth }) => {
    const [token, setToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        const t = token.trim();
        if (!t) {
            setError("Bitte Token eingeben.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const res = await fetch(`${API}/waitlist`, {
                headers: { Authorization: `Bearer ${t}` },
            });
            if (res.status === 401) {
                setError("Token ungültig.");
                setLoading(false);
                return;
            }
            if (!res.ok) {
                setError(`Fehler ${res.status}`);
                setLoading(false);
                return;
            }
            localStorage.setItem(STORAGE_KEY, t);
            onAuth(t);
        } catch {
            setError("Verbindung fehlgeschlagen.");
            setLoading(false);
        }
    };

    return (
        <div style={styles.loginWrap} data-testid="admin-login">
            <form
                onSubmit={onSubmit}
                style={styles.loginCard}
                data-testid="admin-login-form"
            >
                <div style={styles.loginEyebrow}>Plenum · Admin</div>
                <h1 style={styles.loginTitle}>Zugang erforderlich</h1>
                <input
                    type="password"
                    placeholder="ADMIN_TOKEN"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    autoFocus
                    style={styles.input}
                    data-testid="admin-token-input"
                />
                {error && <div style={styles.error}>{error}</div>}
                <button
                    type="submit"
                    disabled={loading}
                    style={{ ...styles.btn, width: "100%" }}
                    data-testid="admin-login-submit"
                >
                    {loading ? "Prüfe…" : "Einloggen"}
                </button>
            </form>
        </div>
    );
};

const Admin = () => {
    const [token, setToken] = useState(() =>
        localStorage.getItem(STORAGE_KEY) || "",
    );
    const [entries, setEntries] = useState(null);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState("");
    const [downloading, setDownloading] = useState(false);

    const authHeader = useMemo(
        () => (token ? { Authorization: `Bearer ${token}` } : {}),
        [token],
    );

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        (async () => {
            try {
                const [listRes, statsRes] = await Promise.all([
                    fetch(`${API}/waitlist`, { headers: authHeader }),
                    fetch(`${API}/waitlist/stats`),
                ]);
                if (listRes.status === 401) {
                    // expired/invalid — drop token, return to login
                    localStorage.removeItem(STORAGE_KEY);
                    if (!cancelled) setToken("");
                    return;
                }
                if (!listRes.ok || !statsRes.ok) {
                    if (!cancelled) setError("Daten konnten nicht geladen werden.");
                    return;
                }
                const list = await listRes.json();
                const s = await statsRes.json();
                if (!cancelled) {
                    setEntries(list);
                    setStats(s);
                }
            } catch {
                if (!cancelled) setError("Verbindung fehlgeschlagen.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token, authHeader]);

    const onLogout = () => {
        localStorage.removeItem(STORAGE_KEY);
        setToken("");
        setEntries(null);
        setStats(null);
    };

    const onExport = async () => {
        setDownloading(true);
        try {
            const res = await fetch(`${API}/waitlist/export.csv`, {
                headers: authHeader,
            });
            if (!res.ok) {
                setError(`CSV-Export fehlgeschlagen (${res.status})`);
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `plenum-waitlist-${new Date()
                .toISOString()
                .slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            setError("CSV-Export fehlgeschlagen.");
        } finally {
            setDownloading(false);
        }
    };

    if (!token) {
        return <LoginScreen onAuth={(t) => setToken(t)} />;
    }

    const loading = entries === null || stats === null;

    return (
        <div style={styles.page} data-testid="admin-dashboard">
            <div style={styles.container}>
                <div style={styles.header}>
                    <div>
                        <p style={styles.subtitle}>Plenum · Admin</p>
                        <h1 style={styles.title}>Waitlist</h1>
                    </div>
                    <button
                        type="button"
                        onClick={onLogout}
                        style={styles.btnGhost}
                        data-testid="admin-logout"
                    >
                        Abmelden
                    </button>
                </div>

                {error && (
                    <div style={{ ...styles.error, marginBottom: "24px" }}>
                        {error}
                    </div>
                )}

                <div style={styles.statsGrid} data-testid="admin-stats">
                    <div style={styles.statCell}>
                        <div style={styles.statLabel}>Total Signups</div>
                        <div
                            style={styles.statValue}
                            data-testid="admin-stat-total"
                        >
                            {loading ? "—" : stats.total}
                        </div>
                    </div>
                    <div style={styles.statCell}>
                        <div style={styles.statLabel}>Letzte 7 Tage</div>
                        <div
                            style={styles.statValue}
                            data-testid="admin-stat-7d"
                        >
                            {loading ? "—" : stats.last_7_days}
                        </div>
                    </div>
                </div>

                <div style={styles.toolbar}>
                    <h2 style={styles.sectionTitle}>
                        Einträge
                        {!loading && (
                            <span
                                style={{
                                    marginLeft: "12px",
                                    color: "#6B7E96",
                                    letterSpacing: "0.05em",
                                }}
                            >
                                ({entries.length})
                            </span>
                        )}
                    </h2>
                    <button
                        type="button"
                        onClick={onExport}
                        disabled={downloading || loading}
                        style={styles.btn}
                        data-testid="admin-export-csv"
                    >
                        {downloading ? "Lädt…" : "CSV-Export"}
                    </button>
                </div>

                <div style={styles.tableWrap}>
                    <table style={styles.table} data-testid="admin-table">
                        <thead>
                            <tr>
                                <th style={styles.th}>E-Mail</th>
                                <th style={styles.th}>Quelle</th>
                                <th style={styles.th}>Zeitstempel</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={3} style={styles.emptyRow}>
                                        Lade…
                                    </td>
                                </tr>
                            )}
                            {!loading && entries.length === 0 && (
                                <tr>
                                    <td colSpan={3} style={styles.emptyRow}>
                                        Noch keine Einträge.
                                    </td>
                                </tr>
                            )}
                            {!loading &&
                                entries.map((e) => (
                                    <tr key={e.id}>
                                        <td style={styles.td}>{e.email}</td>
                                        <td style={styles.tdMono}>
                                            {e.source || "—"}
                                        </td>
                                        <td style={styles.tdMono}>
                                            {formatDate(e.created_at)}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>

                <p style={styles.footerLine}>Plenum © 2026 · Admin</p>
            </div>
        </div>
    );
};

export default Admin;
